import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Plus, Trash2, X, ExternalLink, CheckCircle2, XCircle,
  Image as ImageIcon, Loader2, Pencil,
} from "lucide-react";
import adminApi, { STATIC_ORIGIN } from "./adminApi";

const emptyForm = { banner_page: "" };

// function imageUrl(filename) {
//   if (!filename) return null;
//   return `${STATIC_ORIGIN}/images/bannerimages/${filename}`;
// }
function imageUrl(filename) {
  if (!filename) return null;
   const fileName = filename.split(/[\\/]/).pop();

  return `${STATIC_ORIGIN}/images/bannerimages/${fileName}`;
}



export default function ManageBanner() {
  const [banners, setBanners] = useState([]);
  const [pageOptions, setPageOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const [filterPage, setFilterPage] = useState("");
  const [filterStatus, setFilterStatus] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [filePreviewName, setFilePreviewName] = useState(null);
  const [existingImage, setExistingImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const fileInputRef = useRef(null);

  const [toast, setToast] = useState(null);
  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2600);
  };

  const fetchBanners = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (filterPage) params.bannerpage = filterPage;
      if (filterStatus) params.status = filterStatus;
      const res = await adminApi.get("/admin/banners", { params });
      setBanners(res.data.banners || []);
    } catch (err) {
      setError(err.response?.data?.message || "Could not load banners.");
    } finally {
      setLoading(false);
    }
  };

  const fetchPageOptions = async () => {
    try {
      const res = await adminApi.get("/admin/banners/pages/options");
      setPageOptions(res.data.pages || []);
    } catch (err) {
      console.error("Could not load banner page options:", err);
    }
  };

  useEffect(() => {
    fetchPageOptions();
  }, []);

  useEffect(() => {
    fetchBanners();
    // eslint-disable-next-line
  }, [filterPage, filterStatus]);

  // ── Selection ────────────────────────────────────────────────────────────
  const toggleSelectAll = () => {
    setSelected(selected.length === banners.length ? [] : banners.map((b) => b.banner_id));
  };
  const toggleSelectOne = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // ── Status toggle ────────────────────────────────────────────────────────
  const handleToggleStatus = async (banner, e) => {
    e.stopPropagation();
    const turningOn = banner.status === 0;
    const ok = window.confirm(
      `Are you sure you want to change the status to ${turningOn ? "Active" : "Inactive"}?`
    );
    if (!ok) return;

    try {
      const res = await adminApi.patch(`/admin/banners/${banner.banner_id}/status`);
      setBanners((prev) =>
        prev.map((b) => (b.banner_id === banner.banner_id ? { ...b, status: res.data.status } : b))
      );
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not update status.");
    }
  };

  // ── Bulk delete ──────────────────────────────────────────────────────────
  const handleBulkDelete = async () => {
    if (selected.length === 0) return;
    const ok = window.confirm(
      `Are you sure you want to delete ${selected.length} selected banner(s)? This cannot be undone.`
    );
    if (!ok) return;

    setBulkDeleting(true);
    try {
      await adminApi.post("/admin/banners/bulk-delete", { ids: selected });
      showToast("success", `${selected.length} banner(s) deleted.`);
      setSelected([]);
      fetchBanners();
    } catch (err) {
      showToast("error", err.response?.data?.message || "Could not delete selected banners.");
    } finally {
      setBulkDeleting(false);
    }
  };

  // ── Add / Edit modal ─────────────────────────────────────────────────────
  const openAddModal = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFile(null);
    setFilePreviewName(null);
    setExistingImage(null);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (banner) => {
    setEditingId(banner.banner_id);
    setForm({ banner_page: banner.banner_page });
    setFile(null);
    setFilePreviewName(null);
    setExistingImage(banner.banner_image);
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    if (saving) return;
    setShowModal(false);
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.toLowerCase().slice(f.name.lastIndexOf("."));
    if (![".jpg", ".jpeg", ".png"].includes(ext)) {
      setFormError("Only JPG, JPEG and PNG files are allowed.");
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      setFormError("Image size must not exceed 2MB.");
      return;
    }
    setFormError("");
    setFile(f);
    setFilePreviewName(f.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!form.banner_page) {
      setFormError("Banner Page is required.");
      return;
    }
    if (!editingId && !file) {
      setFormError("Image is required.");
      return;
    }

    const fd = new FormData();
    fd.append("banner_page", form.banner_page);
    if (file) fd.append("image", file);

    setSaving(true);
    try {
      if (editingId) {
        await adminApi.put(`/admin/banners/${editingId}`, fd);
        showToast("success", "Banner updated.");
      } else {
        await adminApi.post("/admin/banners", fd);
        showToast("success", "Banner added.");
      }
      setShowModal(false);
      fetchBanners();
    } catch (err) {
      setFormError(err.response?.data?.message || "Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const allSelected = banners.length > 0 && selected.length === banners.length;
  // console.log("chandra");
  // console.log(banners);//return false;

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 shrink-0 mb-2">
        <div>
          <h3 className="font-display font-bold text-[16px] bg-gradient-to-r from-[#0B2545] to-[#0EA5A4] bg-clip-text text-transparent tracking-tight">
            Manage Banners
          </h3>
        </div>
		        <div className="flex items-center gap-1.5">
          <label className="text-xs font-medium text-slate-500 whitespace-nowrap">
            Banner For:
          </label>
          <select
            value={filterPage}
            onChange={(e) => setFilterPage(e.target.value)}
            className="min-w-[180px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
          >
            <option value="">-- All Pages --</option>
            {pageOptions.map((p) => (
              <option key={p.parid} value={p.parid}>
                {p.par_value}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1.5">
          <label className="text-xs font-medium text-slate-500 whitespace-nowrap">
            Status:
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="min-w-[130px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
          >
            <option value="">-- All Status --</option>
            <option value="1">Active</option>
            <option value="2">Inactive</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <AnimatePresence>
            {selected.length > 0 && (
              <motion.button
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex items-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 text-sm font-medium px-3.5 py-2 rounded-lg transition disabled:opacity-60"
              >
                {bulkDeleting ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                Delete Selected ({selected.length})
              </motion.button>
            )}
          </AnimatePresence>
          <button
            onClick={openAddModal}
            className="flex items-center gap-1.5 bg-[#0EA5A4] hover:bg-[#0B7E7D] text-white text-[13px] font-medium px-3 py-1.5 rounded-lg transition shadow-sm"
          >
            <Plus size={15} />
            Add New Banner
          </button>
        </div>
      </div>

    {/* Filters 
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div className="flex items-center gap-1.5">
          <label className="text-xs font-medium text-slate-500 whitespace-nowrap">
            Banner For:
          </label>
          <select
            value={filterPage}
            onChange={(e) => setFilterPage(e.target.value)}
            className="min-w-[180px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
          >
            <option value="">-- All Pages --</option>
            {pageOptions.map((p) => (
              <option key={p.parid} value={p.parid}>
                {p.par_value}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1.5">
          <label className="text-xs font-medium text-slate-500 whitespace-nowrap">
            Status:
          </label>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="min-w-[130px] border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs bg-white outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition"
          >
            <option value="">-- All Status --</option>
            <option value="1">Active</option>
            <option value="2">Inactive</option>
          </select>
        </div>
      </div>*/}
      {/* Table card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex-1 min-h-0 flex flex-col overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400 text-sm gap-2">
            <Loader2 size={18} className="animate-spin" /> Loading banners...
          </div>
        ) : error ? (
          <div className="py-16 text-center text-sm text-red-500">{error}</div>
        ) : banners.length === 0 ? (
          <div className="py-16 text-center">
            <ImageIcon size={28} className="mx-auto text-slate-300 mb-2" />
            <p className="text-sm text-slate-400">No banners yet — click "Add New Banner" to create one.</p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 z-10 shadow-md">
                <tr className="text-left text-base">
                  <th className="th-colorful px-3 py-3 pl-3 w-8 border-b-2 border-r normal-case" >
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                    />
                  </th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r">Banner Page</th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide border-b-2 border-r" style={{textAlign:"center"}}>Image</th>
                  <th className="th-colorful px-3 py-3 font-bold uppercase tracking-wide text-center border-b-2" >Status</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {banners.map((b, i) => (
                    <motion.tr
                      key={b.banner_id}
                      layout
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: i * 0.02 }}
                      onClick={() => openEditModal(b)}
                      className={`cursor-pointer hover:bg-[#0EA5A4]/30 transition-colors duration-150 border-b border-slate-200 ${
                        selected.includes(b.banner_id) ? "bg-[#0EA5A4]/15" : ""
                      }`}
                    >
                      <td className="px-3 py-2 border-r border-slate-200" onClick={(e) => e.stopPropagation()} >
                        <input
                          type="checkbox"
                          checked={selected.includes(b.banner_id)}
                          onChange={() => toggleSelectOne(b.banner_id)}
                          className="w-4 h-4 rounded border-slate-300 accent-[#0EA5A4] cursor-pointer"
                        />
                      </td>
                      <td className="px-3 py-2  text-slate-800 font-medium border-r border-slate-200">{b.banner_page_name || b.banner_page}</td>
                      <td className="px-3 py-2  border-r border-slate-200" style={{textAlign:"center"}}>
                     {/* {b.banner_image} */}
                      {b.banner_image ? (
                          <a
                            href={imageUrl(b.banner_image)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="inline-flex items-center gap-1.5 text-[#0EA5A4] hover:underline"
                          >
                            {/* <ImageIcon size={14} className="shrink-0" />
                            View
                            <ExternalLink size={11} className="shrink-0" /> */}
                               <div style={{border:"0.5px solid #999",padding:"7px",marginTop:"3px",marginBottom:"3px",borderRadius:"7px"}}><i style={{color:"green"}} class="fa fa-eye" aria-hidden="true"></i></div>
                          </a>
                        ) : (
                          <span className="text-slate-300 text-xs">None</span>
                        )}
                      </td>
                     
                      <td className="px-3 py-2 " >
                      <div className="flex items-center justify-center">
                        <motion.button
                          whileTap={{ scale: 0.9 }}
                          onClick={(e) => handleToggleStatus(b, e)}
                          title={
                            Number(b.status) === 1
                              ? "Active — click to deactivate"
                              : "Inactive — click to activate"
                          }
                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition ${
                            Number(b.status) === 1
                              ? "bg-[#0EA5A4]/10 text-[#0B7E7D] hover:bg-[#0EA5A4]/20"
                              : "bg-slate-100 text-slate-400 hover:bg-slate-200"
                          }`}
                        >
                          {Number(b.status) === 1 ? (
                            <CheckCircle2 size={15} />
                          ) : (
                            <XCircle size={15} />
                          )}
                          {Number(b.status) === 1 ? "Active" : "Inactive"}
                        </motion.button>
                      </div>
                    </td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
              toast.type === "success" ? "bg-[#0B2545] text-white" : "bg-red-600 text-white"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / Edit modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
            className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 12 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
                <h4 className="font-display font-semibold text-[#0B2545]">
                  {editingId ? "Edit Banner" : "Add New Banner"}
                </h4>
                <button onClick={closeModal} className="text-slate-400 hover:text-slate-700">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Banner Page <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={form.banner_page}
                    onChange={(e) => setForm({ ...form, banner_page: e.target.value })}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0EA5A4]/30 focus:border-[#0EA5A4] transition bg-white"
                  >
                    <option value="">--Select--</option>
                    {pageOptions.map((p) => (
                      <option key={p.parid} value={p.parid}>
                        {p.par_value}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Only one banner is allowed per page (except the configured Home page).
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1.5 block">
                    Image — JPG, JPEG or PNG, max 2MB (resized to 1280×370){" "}
                    <span className="text-red-500">*</span>
                  </label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-3 border border-dashed border-slate-300 rounded-lg px-3 py-3 cursor-pointer hover:border-[#0EA5A4] transition"
                  >
                    <div className="w-16 h-10 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                      <ImageIcon size={18} className="text-slate-300" />
                    </div>
                    <div className="text-xs text-slate-500">
                      {filePreviewName
                        ? filePreviewName
                        : existingImage
                        ? "Click to replace image"
                        : "Click to choose an image"}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                </div>

                {formError && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    {formError}
                  </p>
                )}

                <div className="flex items-center gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-[#0B2545] hover:bg-[#143A63] text-white font-medium rounded-lg py-2.5 transition disabled:opacity-60"
                  >
                    {saving ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : editingId ? (
                      <Pencil size={15} />
                    ) : (
                      <Plus size={15} />
                    )}
                    {saving ? "Saving..." : editingId ? "Update" : "Submit"}
                  </button>
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2.5 rounded-lg text-sm font-medium text-slate-500 hover:bg-slate-50 transition"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
